export type HeadPose = {
  yaw: number;
  pitch: number;
  roll: number;
  confidence: number;
  timestamp: number;
};

export type CalibratedPoint = {
  x: number;
  y: number;
};

export type TrackingQuality = "good" | "fair" | "lost";

export type HeadInput = CalibratedPoint & {
  isTracked: boolean;
  trackingQuality: TrackingQuality;
  isNeutral: boolean;
  isTooFast: boolean;
  timestamp: number;
};

export type ComfortRanges = {
  left: number;
  right: number;
  up: number;
  down: number;
};

export type ComfortSamples = Record<keyof ComfortRanges, HeadPose[]>;

export type FaceKeypoint = {
  x: number;
  y: number;
  label?: string;
  score?: number;
};

export const MAX_GAME_INPUT_RADIUS = 0.72;
export const TRACKING_LOSS_GRACE_MS = 900;
export const MIN_CALIBRATION_SAMPLES = 12;
export const MIN_COMFORT_SAMPLES = 4;
const HORIZONTAL_COMFORT_RANGE = 40;
const VERTICAL_COMFORT_RANGE = 30;
const DEFAULT_COMFORT_RANGES: ComfortRanges = {
  left: HORIZONTAL_COMFORT_RANGE,
  right: HORIZONTAL_COMFORT_RANGE,
  up: VERTICAL_COMFORT_RANGE,
  down: VERTICAL_COMFORT_RANGE,
};
const INPUT_DEADZONE = 0.035;
const INPUT_IDLE_CUTOFF = 0.55;
const INPUT_ACTIVE_CUTOFF = 4.2;
const INPUT_ACTIVE_DISTANCE = 0.2;
const INPUT_MAX_SPEED = 1.65;

const FACE_KEYPOINT = {
  firstEye: 0,
  secondEye: 1,
  nose: 2,
  mouth: 3,
  firstTragion: 4,
  secondTragion: 5,
} as const;

export class FaceTrackingContinuity {
  private firstMissAt: number | null = null;

  detected() {
    this.firstMissAt = null;
  }

  missed(timestamp: number): "recovering" | "lost" {
    this.firstMissAt ??= timestamp;
    return timestamp - this.firstMissAt >= TRACKING_LOSS_GRACE_MS ? "lost" : "recovering";
  }

  reset() {
    this.firstMissAt = null;
  }
}

export function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function poseFromDetection(keypoints: FaceKeypoint[], confidence: number, timestamp: number): HeadPose | null {
  const firstEye = keypoints[FACE_KEYPOINT.firstEye];
  const secondEye = keypoints[FACE_KEYPOINT.secondEye];
  const nose = keypoints[FACE_KEYPOINT.nose];
  const mouth = keypoints[FACE_KEYPOINT.mouth];
  const firstTragion = keypoints[FACE_KEYPOINT.firstTragion];
  const secondTragion = keypoints[FACE_KEYPOINT.secondTragion];
  if (!firstEye || !secondEye || !nose || !mouth || !firstTragion || !secondTragion) return null;

  const eyes = [firstEye, secondEye].sort((a, b) => a.x - b.x);
  const tragions = [firstTragion, secondTragion].sort((a, b) => a.x - b.x);
  const eyeMidpoint = {
    x: (eyes[0].x + eyes[1].x) / 2,
    y: (eyes[0].y + eyes[1].y) / 2,
  };
  const tragionMidpoint = {
    x: (tragions[0].x + tragions[1].x) / 2,
    y: (tragions[0].y + tragions[1].y) / 2,
  };
  const faceWidth = Math.abs(tragions[1].x - tragions[0].x);
  const eyeToMouth = mouth.y - eyeMidpoint.y;
  if (faceWidth < 0.03) return null;

  const horizontalOffset = (nose.x - tragionMidpoint.x) / faceWidth;
  // Looking up can visually compress the eye-to-mouth distance. A width-derived
  // floor keeps the six-point pitch estimate responsive instead of rejecting
  // an otherwise valid face at the top of the comfortable range.
  const verticalScale = Math.max(Math.abs(eyeToMouth), faceWidth * 0.16);
  const verticalRatio = (nose.y - eyeMidpoint.y) / verticalScale;
  const eyeDeltaX = eyes[1].x - eyes[0].x;
  const eyeDeltaY = eyes[1].y - eyes[0].y;
  const degrees = 180 / Math.PI;

  return {
    yaw: clamp(-horizontalOffset * 100, -45, 45),
    pitch: clamp(verticalRatio * 100, -20, 120),
    roll: Math.atan2(eyeDeltaY, eyeDeltaX) * degrees,
    confidence: clamp(confidence, 0, 1),
    timestamp,
  };
}

export function calibratedPoint(
  pose: Pick<HeadPose, "yaw" | "pitch">,
  baseline: Pick<HeadPose, "yaw" | "pitch">,
  ranges: ComfortRanges = DEFAULT_COMFORT_RANGES,
): CalibratedPoint {
  const yawDelta = pose.yaw - baseline.yaw;
  const pitchDelta = pose.pitch - baseline.pitch;
  const point = {
    x: yawDelta / (yawDelta < 0 ? ranges.left : ranges.right),
    y: pitchDelta / (pitchDelta < 0 ? ranges.up : ranges.down),
  };
  return constrainPointToOrbit(point);
}

function directionalRange(values: number[], minimum: number) {
  if (values.length < MIN_COMFORT_SAMPLES) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const value = sorted[Math.floor((sorted.length - 1) * 0.8)];
  return value >= minimum ? value : null;
}

export function comfortDirectionDeltas(baseline: Pick<HeadPose, "yaw" | "pitch">, direction: keyof ComfortRanges, samples: HeadPose[]) {
  const axis = direction === "left" || direction === "right" ? "yaw" : "pitch";
  const sign = direction === "left" || direction === "up" ? -1 : 1;
  return samples.map((sample) => (sample[axis] - baseline[axis]) * sign).filter((value) => value > 0);
}

export function comfortRangesFromSamples(baseline: Pick<HeadPose, "yaw" | "pitch">, samples: ComfortSamples): ComfortRanges | null {
  const left = directionalRange(comfortDirectionDeltas(baseline, "left", samples.left), 6);
  const right = directionalRange(comfortDirectionDeltas(baseline, "right", samples.right), 6);
  const up = directionalRange(comfortDirectionDeltas(baseline, "up", samples.up), 4);
  const down = directionalRange(comfortDirectionDeltas(baseline, "down", samples.down), 4);
  if (left === null || right === null || up === null || down === null) return null;
  return { left, right, up, down };
}

function applyDeadzone(value: number) {
  const magnitude = Math.abs(value);
  if (magnitude <= INPUT_DEADZONE) return 0;
  return Math.sign(value) * ((magnitude - INPUT_DEADZONE) / (1 - INPUT_DEADZONE));
}

function smoothingAlpha(cutoff: number, elapsedSeconds: number) {
  const timeConstant = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + timeConstant / elapsedSeconds);
}

class AdaptiveScalarFilter {
  private raw: number | null = null;
  private filtered: number | null = null;
  private derivative = 0;

  next(value: number, elapsedSeconds: number, confidence: number) {
    if (this.raw === null || this.filtered === null) {
      this.raw = value;
      this.filtered = value;
      return value;
    }
    // A deliberately slower derivative estimate stops alternating landmark
    // noise from being mistaken for a fast head turn. Confidence then tightens
    // the filter when the detector has less light or detail to work with.
    const derivativeAlpha = smoothingAlpha(0.28, elapsedSeconds);
    const rawDerivative = (value - this.raw) / elapsedSeconds;
    this.derivative += derivativeAlpha * (rawDerivative - this.derivative);
    const confidenceWeight = clamp(confidence, 0, 1);
    const cutoff = 0.3
      + 0.13 * confidenceWeight
      + 0.04 * Math.abs(this.derivative) * (0.4 + 0.6 * confidenceWeight);
    const alpha = smoothingAlpha(cutoff, elapsedSeconds);
    this.raw = value;
    this.filtered += alpha * (value - this.filtered);
    return this.filtered;
  }

  reset() {
    this.raw = null;
    this.filtered = null;
    this.derivative = 0;
  }
}

export class AdaptivePoseFilter {
  private readonly yaw = new AdaptiveScalarFilter();
  private readonly pitch = new AdaptiveScalarFilter();
  private readonly roll = new AdaptiveScalarFilter();
  private timestamp: number | null = null;

  next(pose: HeadPose): HeadPose {
    const elapsedSeconds = this.timestamp === null ? 1 / 6 : clamp((pose.timestamp - this.timestamp) / 1_000, 1 / 60, 0.5);
    this.timestamp = pose.timestamp;
    return {
      ...pose,
      yaw: this.yaw.next(pose.yaw, elapsedSeconds, pose.confidence),
      pitch: this.pitch.next(pose.pitch, elapsedSeconds, pose.confidence),
      roll: this.roll.next(pose.roll, elapsedSeconds, pose.confidence),
    };
  }

  reset() {
    this.yaw.reset();
    this.pitch.reset();
    this.roll.reset();
    this.timestamp = null;
  }
}

export function headInputFromPose(
  pose: HeadPose,
  baseline: Pick<HeadPose, "yaw" | "pitch">,
  previous: HeadInput | null,
  ranges?: ComfortRanges,
): HeadInput {
  const calibrated = calibratedPoint(pose, baseline, ranges);
  const rawPoint = constrainPointToOrbit({
    x: applyDeadzone(calibrated.x),
    y: applyDeadzone(calibrated.y),
  });
  const rawElapsedSeconds = previous ? Math.max((pose.timestamp - previous.timestamp) / 1_000, 1 / 60) : 1;
  const elapsedSeconds = Math.min(rawElapsedSeconds, 0.2);
  const distance = previous ? Math.hypot(rawPoint.x - previous.x, rawPoint.y - previous.y) : 0;
  const speed = previous ? distance / rawElapsedSeconds : 0;
  let point = rawPoint;

  if (previous && distance > 0) {
    const confidence = clamp(pose.confidence, 0, 1);
    const jitterRadius = 0.008 + (1 - confidence) * 0.028;
    if (distance <= jitterRadius) {
      point = { x: previous.x, y: previous.y };
    } else {
      const movement = clamp((distance - jitterRadius) / (INPUT_ACTIVE_DISTANCE - jitterRadius), 0, 1);
      const cutoff = INPUT_IDLE_CUTOFF
        + (INPUT_ACTIVE_CUTOFF - INPUT_IDLE_CUTOFF) * movement ** 1.5;
      const filteredStep = distance * smoothingAlpha(cutoff, elapsedSeconds);
      const step = Math.min(filteredStep, INPUT_MAX_SPEED * elapsedSeconds);
      const scale = step / distance;
      point = constrainPointToOrbit({
        x: previous.x + (rawPoint.x - previous.x) * scale,
        y: previous.y + (rawPoint.y - previous.y) * scale,
      });
    }
  }

  const isTooFast = speed > 2.4;
  return {
    ...point,
    isTracked: true,
    trackingQuality: isTooFast ? "fair" : "good",
    isNeutral: Math.hypot(point.x, point.y) <= 0.08,
    isTooFast,
    timestamp: pose.timestamp,
  };
}

export function constrainPointToOrbit(point: CalibratedPoint): CalibratedPoint {
  const radius = Math.hypot(point.x, point.y);
  if (radius <= MAX_GAME_INPUT_RADIUS) return point;
  const scale = MAX_GAME_INPUT_RADIUS / radius;
  return { x: point.x * scale, y: point.y * scale };
}

export function averagePose(samples: HeadPose[]): HeadPose | null {
  if (!samples.length) return null;
  const total = samples.reduce(
    (sum, sample) => ({ yaw: sum.yaw + sample.yaw, pitch: sum.pitch + sample.pitch, roll: sum.roll + sample.roll }),
    { yaw: 0, pitch: 0, roll: 0 },
  );
  const last = samples.at(-1)!;
  return {
    yaw: total.yaw / samples.length,
    pitch: total.pitch / samples.length,
    roll: total.roll / samples.length,
    confidence: Math.min(...samples.map((sample) => sample.confidence)),
    timestamp: last.timestamp,
  };
}

export function stableCalibration(samples: HeadPose[]) {
  const baseline = averagePose(samples);
  if (!baseline || samples.length < MIN_CALIBRATION_SAMPLES) return null;
  const variance = samples.reduce((sum, sample) => (
    sum + (sample.yaw - baseline.yaw) ** 2 + (sample.pitch - baseline.pitch) ** 2
  ), 0) / samples.length;
  return Math.sqrt(variance) <= 3.5 ? baseline : null;
}
